import React from "react";
import { ChevronRight } from "lucide-react";

const TYPE_OPTIONS = [
    { key: "invoice", label: "Invoices" },
    { key: "po", label: "Purchase orders" },
    { key: "shipment", label: "Shipments" },
    { key: "loan", label: "Finance" },
];

export default function ActivityFeed({
    blocks = [],
    count,
    selectedTypes = [],
    filterSummary,
    onChangeFilter,
    Divider,
    FeedRow,
    EmptyState,
    onCta,
}) {
    const [open, setOpen] = React.useState(false);
    const [pending, setPending] = React.useState(selectedTypes);
    const ref = React.useRef(null);

    React.useEffect(() => setPending(selectedTypes), [selectedTypes]);

    React.useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onEsc = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    const summary =
        filterSummary ?? (selectedTypes?.length ? `${selectedTypes.length} selected` : "All");

    const toggleKey = (k) =>
        setPending((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

    const apply = () => {
        onChangeFilter?.(pending);
        setOpen(false);
    };

    const clear = () => {
        onChangeFilter?.([]);
        setPending([]);
        setOpen(false);
    };

    return (
        <>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-[14px] mb-0 font-[500] text-[#161827]">Feed</h3>
                    {typeof count === "number" && (
                        <span className="px-1 py-0.5 rounded-full text-[10px] bg-[#6F739B] text-[#FBFCFE]">
                            {count}
                        </span>
                    )}
                </div>

                <div className="relative" ref={ref}>
                    <button
                        className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-sm text-[#101828]"
                        onClick={() => setOpen((p) => !p)}
                        aria-haspopup="menu"
                        aria-expanded={open}
                    >
                        <span className="text-[#6F739B] font-[400] text-[14px] mr-1">Filter:</span>
                        <span className="text-[#383B56] font-[500] text-[16px]">{summary}</span>
                        <ChevronRight className="h-5 w-5 rotate-90 opacity-60" />
                    </button>

                    {open && (
                        <div
                            role="menu"
                            className="absolute right-0 top-[110%] z-50 w-[300px] rounded-2xl bg-white shadow-md"
                        >
                            <div className="!p-4">
                                <div className="text-[15px] font-semibold text-[#101828] !mb-3">
                                    Type
                                </div>

                                <div className="space-y-3">
                    {TYPE_OPTIONS.map(({ key, label }, index) => {
                                        const checked = pending.includes(key);
                                        return (
                                            <label
                                            key={key + index}
                                                className="flex items-center gap-3 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="appearance-none h-5 w-5 rounded-md border border-[#E5E7EB] grid place-items-center
                                   checked:bg-[#6F00FF] checked:border-[#6F00FF]
                                   before:content-[''] before:h-2.5 before:w-2.5 before:rounded-sm before:bg-white
                                   checked:before:block before:hidden"
                                                    checked={checked}
                                                    onChange={() => toggleKey(key)}
                                                />
                                                <span className="text-[16px] text-[#344054]">
                                                    {label}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={apply}
                                    className="mt-3 w-full h-11 rounded-lg bg-[#6F00FF] text-white text-[15px] font-semibold hover:opacity-95"
                                >
                                    Apply
                                </button>
                            </div>
                            <button
                                onClick={clear}
                                className="mt- w-full h-11 bg-[#F7F7FB] text-[#101828] text-[15px] font-medium"
                            >
                                Clear filter
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {blocks.length === 0 ? (
                <EmptyState className="mt-4" label="Nothing in your feed yet" />
            ) : (
                <div className="h-[50vh] hide-scrollbar overflow-scroll">
                    {blocks.map((block, bi) => (
                        <div key={bi} className="mt-4 first:mt-3">
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">
                                    {block.label}
                                </span>
                                <div className="h-px flex-1 bg-[#EEF0F6]" />
                            </div>

                            <div className="mt-3">
                                {block.items.map((item, i) => (
                                  <React.Fragment key={item.id + i}>
                                        <FeedRow {...item} onCta={onCta} />
                                        {i !== block.items.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
